import { Box } from '@mui/material';
import { Container, ScreenReadersOnly, Stack, Typography } from '@customink/pigment-react';
import { Loader } from '~/components/Loader/loader';
import { usePage } from '~/adapters/browser/usePage';
import { Title } from '~/pages/Settings/title';
import { useGetCertificates, useGetCustomer } from '~/adapters/services/accounts/taxExemptionsECM/hooks';
import { PortalOrBusinessAddressButton } from '~/pages/Settings/tabs/TaxExemptionECM/components/Portal/PortalOrBusinessAddressButton';
import { CushionedBox } from '~/components/Layout/CushionedBox';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { CertificateList } from './components/Certificate/CertificateList';

const ScreenReaderHeading = () => (
    <ScreenReadersOnly>
        <h2>Tax Exemption</h2>
    </ScreenReadersOnly>
);

function TaxExemptionInner() {
    usePage({ title: Title, reportPath: '/accounts/tax_exemption' });

    // Load in parallel here in parent component
    const certificates = useGetCertificates();
    const customer = useGetCustomer();

    if (certificates.isPending || customer.isPending) {
        return <Loader title="Loading..." devLabel="Tax exemption API" />;
    }

    if (customer.isError) {
        throw customer.error;
    }
    if (certificates.isError) {
        throw certificates.error;
    }

    if (certificates.data?.length) {
        return (
            <Box>
                <ScreenReaderHeading />
                <Stack mb={2} gap={1} direction="row" justifyContent="flex-end" flexWrap="wrap">
                    <PortalOrBusinessAddressButton
                        text="Add Or Manage Certificates"
                        variant="secondary"
                        customer={customer.data}
                        onCustomerUpdate={customer.refetch}
                    />
                </Stack>
                <CertificateList certificates={certificates.data} />
            </Box>
        );
    }

    return (
        <Box>
            <ScreenReaderHeading />
            <CushionedBox>
                <Container maxWidth="md">
                    <Typography variant="bodyShort2" paragraph>
                        Once we&apos;ve verified your tax exempt status, you can apply it to qualifying purchases during
                        checkout.
                    </Typography>
                    <PortalOrBusinessAddressButton
                        text="Verify Tax Exempt Status"
                        variant="primary"
                        customer={customer.data}
                        onCustomerUpdate={customer.refetch}
                    />
                </Container>
            </CushionedBox>
        </Box>
    );
}

export function TaxExemptionTab() {
    return (
        <ErrorBoundary errorMessage="An error occured while loading your Tax Exemptions overview">
            <TaxExemptionInner />
        </ErrorBoundary>
    );
}
